'use client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

// Importaciones del servicio de Citas
import {
  fetchAllAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  AppointmentAPI,
  AppointmentData,
  AppointmentType,
  AppointmentStatus,
} from './appointment'; // Asegúrate de que la ruta sea correcta

// Componentes Separados
import AppointmentFormModal from './components/AppointmentFormModal';
import AppointmentList from './components/AppointmentList';
import { getHeaderConfig } from '../Users/Users'; // Reutilizamos la configuración del Header

// --- Estado Inicial del Formulario de Agendamiento ---
const INITIAL_FORM_DATA: AppointmentData = {
  patientIdCode: '',
  date: '', // Formato: YYYY-MM-DDTHH:mm
  reason: '',
  type: 'CONSULTA' as AppointmentType,
  status: 'PENDIENTE' as AppointmentStatus,
  notes: '',
};

function AppointmentPanel({ servicesManager }) {
  const [appointments, setAppointments] = useState<AppointmentAPI[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<AppointmentAPI | null>(null);
  const [formData, setFormData] = useState<AppointmentData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { show } = useModal();
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();

  // Configuración del Header
  const { menuOptions } = getHeaderConfig({
    servicesManager,
    t,
    show,
    navigate,
    appConfig,
  });

  // --- Lógica de Seguridad y Carga de Datos ---
  useEffect(() => {
    // Seguridad: Redirigir si no hay token (reutilizado del componente anterior)
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;
    if (!session?.token) {
      toast.warning('Hubo un error, no tienes permisos para esta página.');
      navigate('/');
    }
  }, [navigate]);

  // Cargar Citas
  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      const apiAppointments = await fetchAllAppointments();
      setAppointments(apiAppointments);
    } catch (error) {
      toast.error('Error al obtener citas: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
  }, []);

  // --- Lógica de Filtro ---
  const filteredAppointments = appointments.filter(
    appt =>
      appt.patientIdCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appt.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appt.patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appt.patient.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Manejadores CRUD ---

  const handleCreateAppointment = async () => {
    try {
      const response = await createAppointment(formData);
      toast.success(`Cita agendada para el paciente ${response.patientIdCode}.`);
      await loadAppointments();
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear cita: ' + (err.message || err));
    }
  };

  const handleUpdateAppointment = async () => {
    if (!editingAppointment) {
      return;
    }
    try {
      // Solo enviamos los campos que pueden ser actualizados
      const updateData: Partial<AppointmentData> = {
        date: formData.date,
        reason: formData.reason,
        type: formData.type,
        status: formData.status,
        notes: formData.notes,
      };

      const response = await updateAppointment(editingAppointment._id, updateData);
      toast.success(`Cita para ${response.patientIdCode} actualizada.`);
      await loadAppointments();
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar cita: ' + (err.message || err));
    }
  };

  const handleDeleteAppointment = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta cita?')) {
      return;
    }
    try {
      await deleteAppointment(id);
      toast.success('Cita eliminada correctamente.');
      setAppointments(prev => prev.filter(appt => appt._id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar la cita: ' + (err.message || err));
    }
  };

  const handleEditAppointment = (appointment: AppointmentAPI) => {
    setEditingAppointment(appointment);
    // Mapear AppointmentAPI a AppointmentData para el formulario
    setFormData({
      patientIdCode: appointment.patientIdCode,
      date: appointment.date.slice(0, 16), // Convertir a formato datetime-local (YYYY-MM-DDTHH:mm)
      reason: appointment.reason,
      type: appointment.type,
      status: appointment.status,
      notes: appointment.notes || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingAppointment(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingAppointment ? handleUpdateAppointment() : handleCreateAppointment();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Citas Médicas</h1>
            <p className="mt-1">Agenda y administra las citas de pacientes</p>
          </div>
          <button
            onClick={() => {
              setEditingAppointment(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Agendar Nueva Cita
          </button>
        </div>

        {/* Lista de Citas */}
        <AppointmentList
          appointments={filteredAppointments}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          handleEditAppointment={handleEditAppointment}
          handleDeleteAppointment={handleDeleteAppointment}
        />
      </div>

      {/* Modal de Formulario */}
      <AppointmentFormModal
        isDialogOpen={isDialogOpen}
        editingAppointment={editingAppointment}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
      />
    </div>
  );
}

AppointmentPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default AppointmentPanel;
